import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";

export type WordListType = "GROUPED_SYNONYMS" | "DICTIONARY_AZ" | string;

export interface WordListRequest {
  listId: string;
  title: string;
  listNumber?: number | null;
  icon?: string;
  colorTheme?: string;
  listType: WordListType;
}

export interface UpdateWordListRequest extends WordListRequest {
  id?: string;
}

export interface WordListItemResponse {
  id: string;
  listId: string;
  title: string;
  listNumber?: number | null;
  icon?: string;
  colorTheme?: string;
  listType: string;
  createdOn?: string;
  lastUpdateOn?: string;
  createdByName?: string;
  createdByEmail?: string;
  groupCount?: number;
  wordCount?: number;
}

export interface CreateWordListResponse extends APIResponse {
  response: WordListItemResponse;
}

export interface ListDropdownItem {
  id: string;
  listId: string;
  title: string;
  name?: string;
  listType?: string;
}

export interface ListDropdownResponse extends APIResponse {
  response: ListDropdownItem[];
}

export interface GroupDropdownItem {
  id: string;
  title: string;
  name?: string;
  listId?: string;
  listName?: string;
}

export interface GroupDropdownResponse extends APIResponse {
  response: GroupDropdownItem[];
}

export interface WordGroupRequest {
  title: string;
  groupOrderNumber?: number | null;
  listId: string; // The database 'id' of the list
  listName?: string;
  description?: string;
}

export interface UpdateWordGroupRequest extends WordGroupRequest {
  id: string; // The database 'id' of the group being updated
}

export interface WordGroupResponseItem {
  id: string;
  title: string;
  groupOrderNumber?: number | null;
  listId: string;
  listName?: string;
  description?: string;
  createdOn?: string;
  lastUpdateOn?: string;
  createdByName?: string;
  createdByEmail?: string;
  wordCount?: number;
}

export interface CreateWordGroupResponse extends APIResponse {
  response: WordGroupResponseItem;
}

export interface WordRequest {
  word: string;
  phonetic?: string;
  partOfSpeech?: string;
  difficultyLevel?: string;
  tags?: string[];
  definition: string;
  exampleSentence?: string;
  synonyms?: string[];
  antonyms?: string[];
  premiumContent?: boolean;
  groupId: string;
  flagged?: boolean;
  listId: string;
}

export interface LearnProgressRequest {
  wordId: string;
  isFlagged: boolean;
  difficultyType?: string;
  listId?: string;
  groupId?: string;
  word?: WordRequest;
}

export interface FlashcardProgressRequest {
  wordId: string;
  feedback: string;
  difficultyType?: string;
  listId?: string;
  groupId?: string;
  word?: WordRequest;
}

export interface UpdateWordRequest extends WordRequest {
  id: string;
}

export interface WordResponseItem {
  id: string;
  word: string;
  phonetic?: string;
  partOfSpeech?: string;
  difficultyLevel?: string;
  tags?: string[];
  definition: string;
  exampleSentence?: string;
  synonyms?: string[];
  antonyms?: string[];
  premiumContent?: boolean;
  groupId: string;
  flagged?: boolean;
  listId: string;
  createdOn?: string;
  lastUpdateOn?: string;
}

export interface CreateWordResponse extends APIResponse {
  response: WordResponseItem;
}

export interface PageableStructure {
  pageNumber: number;
  pageSize: number;
  sort: unknown[];
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface GetAllWordListsResponse extends APIResponse {
  response: {
    content: WordListItemResponse[];
    pageable?: PageableStructure;
    last?: boolean;
    totalElements?: number;
    totalPages?: number;
    size?: number;
    number?: number;
    sort?: unknown[];
    numberOfElements?: number;
    first?: boolean;
    empty?: boolean;
  };
}

export interface GetAllWordGroupsResponse extends APIResponse {
  response: {
    content: WordGroupResponseItem[];
    pageable?: PageableStructure;
    last?: boolean;
    totalElements?: number;
    totalPages?: number;
    size?: number;
    number?: number;
    sort?: unknown[];
    numberOfElements?: number;
    first?: boolean;
    empty?: boolean;
  };
}

export interface GetAllWordsResponse extends APIResponse {
  response: {
    content: WordResponseItem[];
    pageable?: PageableStructure;
    last?: boolean;
    totalElements?: number;
    totalPages?: number;
    size?: number;
    number?: number;
    sort?: unknown[];
    numberOfElements?: number;
    first?: boolean;
    empty?: boolean;
  };
}

export interface WordMcqResponse {
  id: string;
  word: string;
  phonetic?: string;
  partOfSpeech?: string;
  groupId?: string;
  listId?: string;
  difficultyLevel?: string;
  correctOption: string;
  options: string[];
}

export interface GetMockTestParams {
  listId?: string;
  groupId?: string;
  difficultyLevel?: string;
  count?: number;
}

export interface MockTestApiResponse extends APIResponse {
  response: WordMcqResponse[];
}

export const WordzAppApiSlice = createApi({
  reducerPath: "wordzAppApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WordList", "WordGroup", "Word"],
  endpoints: (builder) => ({
    getMockTestWords: builder.query<
      MockTestApiResponse,
      GetMockTestParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.listId && params.listId !== "ALL") {
          queryParams.append("listId", params.listId);
        }
        if (params?.groupId && params.groupId !== "ALL") {
          queryParams.append("groupId", params.groupId);
        }
        if (params?.difficultyLevel && params.difficultyLevel !== "ALL") {
          queryParams.append("difficultyLevel", params.difficultyLevel);
        }
        if (params?.count) {
          queryParams.append("count", String(params.count));
        }
        const qs = queryParams.toString();
        return {
          url: qs ? `word/mock-test?${qs}` : "word/mock-test",
          method: "GET",
        };
      },
    }),
    createWordList: builder.mutation<CreateWordListResponse, WordListRequest>({
      query: (body) => ({
        url: "word-list",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WordList"],
    }),
    updateWordList: builder.mutation<
      CreateWordListResponse,
      UpdateWordListRequest
    >({
      query: (body) => ({
        url: body.id
          ? `word-list?id=${encodeURIComponent(body.id)}`
          : "word-list",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WordList"],
    }),
    deleteWordList: builder.mutation<APIResponse, string>({
      query: (id) => ({
        url: `word-list?id=${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WordList"],
    }),
    getAllWordLists: builder.query<
      GetAllWordListsResponse,
      { pageNo?: number; pageSize?: number } | void
    >({
      query: (params) => {
        const page = params?.pageNo ?? 0;
        const size = params?.pageSize ?? 20;
        return {
          url: `word-list/get-all?pageNo=${page}&pageSize=${size}`,
          method: "GET",
        };
      },
      providesTags: ["WordList"],
    }),
    getListDropdown: builder.query<ListDropdownResponse, void>({
      query: () => ({
        url: "word-list/dropdown",
        method: "GET",
      }),
      providesTags: ["WordList"],
    }),
    getGroupDropdownByList: builder.query<GroupDropdownResponse, string>({
      query: (listId) => ({
        url: `word-group/dropdown/by-list?listId=${encodeURIComponent(listId)}`,
        method: "GET",
      }),
      providesTags: ["WordGroup"],
    }),
    getListTypeEnum: builder.query<APIResponse & { response: string[] }, void>({
      query: () => ({
        url: "common/ListType",
        method: "GET",
      }),
    }),
    getPartOfSpeechEnum: builder.query<
      APIResponse & { response: string[] },
      void
    >({
      query: () => ({
        url: "common/PartOfSpeech",
        method: "GET",
      }),
    }),
    getDifficultyLevelEnum: builder.query<
      APIResponse & { response: string[] },
      void
    >({
      query: () => ({
        url: "common/DifficultyLevel",
        method: "GET",
      }),
    }),
    createWordGroup: builder.mutation<
      CreateWordGroupResponse,
      WordGroupRequest
    >({
      query: (body) => ({
        url: "word-group",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : ["WordGroup", "WordList"],
    }),
    updateWordGroup: builder.mutation<
      CreateWordGroupResponse,
      UpdateWordGroupRequest
    >({
      query: (body) => ({
        url: body.id
          ? `word-group?id=${encodeURIComponent(body.id)}`
          : "word-group",
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : ["WordGroup", "WordList"],
    }),
    deleteWordGroup: builder.mutation<APIResponse, string>({
      query: (id) => ({
        url: `word-group?id=${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : ["WordGroup", "WordList"],
    }),
    getAllWordGroups: builder.query<
      GetAllWordGroupsResponse,
      { listId: string; pageNo?: number; pageSize?: number }
    >({
      query: ({ listId, pageNo = 0, pageSize = 20 }) => ({
        url: `word-group/by-list?listId=${encodeURIComponent(listId)}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["WordGroup"],
    }),
    createWord: builder.mutation<CreateWordResponse, WordRequest>({
      query: (body) => ({
        url: "word",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ["Word", "WordGroup"]),
    }),
    updateWord: builder.mutation<CreateWordResponse, UpdateWordRequest>({
      query: (body) => ({
        url: body.id ? `word?id=${encodeURIComponent(body.id)}` : "word",
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ["Word", "WordGroup"]),
    }),
    deleteWord: builder.mutation<APIResponse, string>({
      query: (id) => ({
        url: `word?id=${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error) => (error ? [] : ["Word", "WordGroup"]),
    }),
    getAllWordsByGroup: builder.query<
      GetAllWordsResponse,
      { groupId: string; pageNo?: number; pageSize?: number }
    >({
      query: ({ groupId, pageNo = 0, pageSize = 20 }) => ({
        url: `word/by-group?groupId=${encodeURIComponent(groupId)}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["Word"],
    }),
    getStudentLearnWords: builder.query<
      APIResponse,
      { difficultyLevel?: string; listId?: string; isFlag?: boolean } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.difficultyLevel) {
          queryParams.append("difficultyLevel", params.difficultyLevel);
        }
        if (params?.listId) {
          queryParams.append("listId", params.listId);
        }
        if (params?.isFlag !== undefined) {
          queryParams.append("isFlag", String(params.isFlag));
        }
        const qs = queryParams.toString();
        const baseUrl = params?.isFlag
          ? "student/learn-progress"
          : "word/learn";
        return {
          url: qs ? `${baseUrl}?${qs}` : baseUrl,
          method: "GET",
        };
      },
    }),
    getStudentFlashcards: builder.query<
      APIResponse,
      { difficultyLevel?: string; listId?: string } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.difficultyLevel) {
          queryParams.append("difficultyLevel", params.difficultyLevel);
        }
        if (params?.listId) {
          queryParams.append("listId", params.listId);
        }
        const qs = queryParams.toString();
        return {
          url: qs ? `student/flashcard?${qs}` : "student/flashcard",
          method: "GET",
        };
      },
    }),
    saveLearnProgress: builder.mutation<APIResponse, LearnProgressRequest>({
      query: (body) => ({
        url: "student/learn-progress",
        method: "POST",
        body,
      }),
    }),
    saveFlashcardProgress: builder.mutation<
      APIResponse,
      FlashcardProgressRequest
    >({
      query: (body) => ({
        url: "student/flashcard",
        method: "POST",
        body,
      }),
    }),
    getStudentFlashcardsByFeedback: builder.query<
      APIResponse,
      { feedback?: string; difficultyLevel?: string; listId?: string } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.feedback && params.feedback !== "ALL") {
          queryParams.append("feedback", params.feedback);
        }
        if (params?.difficultyLevel && params.difficultyLevel !== "ALL") {
          queryParams.append("difficultyLevel", params.difficultyLevel);
        }
        if (params?.listId && params.listId !== "ALL") {
          queryParams.append("listId", params.listId);
        }
        const qs = queryParams.toString();
        return {
          url: qs
            ? `student/flashcard/by-feedback?${qs}`
            : "student/flashcard/by-feedback",
          method: "GET",
        };
      },
    }),
    getBulkUploadWordsTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "word/bulkUpload/template",
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
    bulkUploadWords: builder.mutation<APIResponse, FormData>({
      query: (formData) => ({
        url: "word/bulkUpload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["WordList", "WordGroup", "Word"],
    }),
  }),
});

export const {
  useCreateWordListMutation,
  useUpdateWordListMutation,
  useDeleteWordListMutation,
  useGetAllWordListsQuery,
  useGetListDropdownQuery,
  useGetGroupDropdownByListQuery,
  useGetListTypeEnumQuery,
  useGetPartOfSpeechEnumQuery,
  useGetDifficultyLevelEnumQuery,
  useCreateWordGroupMutation,
  useUpdateWordGroupMutation,
  useDeleteWordGroupMutation,
  useGetAllWordGroupsQuery,
  useCreateWordMutation,
  useUpdateWordMutation,
  useDeleteWordMutation,
  useGetAllWordsByGroupQuery,
  useGetStudentLearnWordsQuery,
  useGetStudentFlashcardsQuery,
  useSaveLearnProgressMutation,
  useSaveFlashcardProgressMutation,
  useGetStudentFlashcardsByFeedbackQuery,
  useGetMockTestWordsQuery,
  useLazyGetMockTestWordsQuery,
  useGetBulkUploadWordsTemplateQuery,
  useLazyGetBulkUploadWordsTemplateQuery,
  useBulkUploadWordsMutation,
} = WordzAppApiSlice;
